import type { ReactNode } from 'react';
import type { ChangeSetView, FieldDiff, Step } from '../api/client';
import { REVERSIBILITY, STATUS, STEP_STATE, actorLabel, dateOnly, fieldName, fullWhen, shortWhen } from '../lib/format';
import { Chip, Notice } from './ui';
import { Icon } from '../lib/icons';

export function StatusChip({ status, small }: { status: ChangeSetView['status']; small?: boolean }) {
  const s = STATUS[status];
  return (
    <Chip tone={s.tone} small={small}>
      {s.label}
    </Chip>
  );
}

export function SourceChip({ cs }: { cs: Pick<ChangeSetView, 'source' | 'undoOf'> }) {
  if (cs.undoOf) return <Chip small>Undo</Chip>;
  if (cs.source === 'leland') return <Chip small tone="brand">Leland</Chip>;
  return null;
}

/** The outcome of an apply or undo, in plain language. */
export function Outcome({ cs }: { cs: ChangeSetView }) {
  if (cs.status === 'applied') {
    const sent = cs.steps.filter((s) => s.reversibility === 'none' && s.state === 'skipped');
    return (
      <Notice tone="ok" title={cs.undoOf ? 'Undone' : 'Done. Every change was checked on the phone system.'}>
        {cs.undoUntil && !cs.undoOf && <>You can undo this until {dateOnly(cs.undoUntil)}.</>}
        {cs.undoOf && <>The settings are back to how they were.</>}
        {sent.length > 0 && <> {sent.map((s) => s.error).join(' ')}</>}
      </Notice>
    );
  }
  if (cs.status === 'rolled_back') return <Notice tone="warn" title="Not applied">{cs.error}</Notice>;
  if (cs.status === 'needs_attention') return <Notice tone="crit" title="Needs attention">{cs.error}</Notice>;
  if (cs.status === 'stale') return <Notice tone="warn" title="Out of date">Something this touches was changed after it was planned. Review it again.</Notice>;
  return null;
}

export function StepList({ steps, showState }: { steps: Step[]; showState?: boolean }) {
  return (
    <ol className="steps">
      {steps.map((s, i) => {
        const st = STEP_STATE[s.state];
        const numTone = !showState ? '' : s.state === 'applied' ? 'ok' : s.state === 'failed' ? 'crit' : s.state === 'rolled_back' ? 'warn' : '';
        const rev = REVERSIBILITY[s.reversibility];
        return (
          <li className="step" key={s.id}>
            <span className={`step-num ${numTone}`} aria-hidden="true">
              {showState && s.state === 'applied' ? <Icon name="check" size={14} strokeWidth={3} /> : i + 1}
            </span>
            <div className="step-body">
              <span className="step-title">{s.summary}</span>
              {s.why && <span className="step-why">{s.why}</span>}
              <span className="step-meta">
                {showState && <Chip small tone={st.tone}>{st.label}</Chip>}
                <Chip small tone={rev.tone}>{rev.label}</Chip>
              </span>
              {s.caveat && s.reversibility !== 'full' && <span className="step-note">{s.caveat}</span>}
              {s.error && <span className="step-error">{s.error}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function ConflictList({ conflicts }: { conflicts: FieldDiff[] }) {
  return (
    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
      {conflicts.map((c, i) => (
        <li key={i}>
          {c.field === 'exists'
            ? `${c.summary}: it is now ${c.actual}, not ${c.expected}.`
            : c.field === 'other settings'
              ? `${c.summary}: other settings on it have changed since.`
              : `${c.summary}: ${c.label ?? fieldName(c.field)} is now “${c.actual || '(none)'}”.`}
        </li>
      ))}
    </ul>
  );
}

/** Full detail of a change set, for history and admin activity. */
export function ChangeSetDetail({ cs, extra }: { cs: ChangeSetView; extra?: ReactNode }) {
  return (
    <>
      {cs.status !== 'applied' && <Outcome cs={cs} />}
      <dl className="dl">
        <dt>Status</dt>
        <dd>
          <StatusChip status={cs.status} />
        </dd>
        <dt>Requested by</dt>
        <dd>
          {actorLabel(cs)} <SourceChip cs={cs} />
        </dd>
        <dt>Planned</dt>
        <dd>{fullWhen(cs.createdAt)}</dd>
        {cs.appliedAt && (
          <>
            <dt>Applied</dt>
            <dd>{fullWhen(cs.appliedAt)}</dd>
          </>
        )}
        {cs.status === 'applied' && cs.undoUntil && (
          <>
            <dt>Undo until</dt>
            <dd>{dateOnly(cs.undoUntil)}</dd>
          </>
        )}
        <dt>Tenant</dt>
        <dd className="mono">{cs.tenant}</dd>
        <dt>Reference</dt>
        <dd className="mono">{cs.id}</dd>
      </dl>
      <div>
        <h3 className="section-title">Steps</h3>
        <div style={{ marginTop: 10 }}>
          <StepList steps={cs.steps} showState={cs.status !== 'planned'} />
        </div>
      </div>
      {cs.notes.length > 0 && (
        <Notice title="Good to know">
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {cs.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </Notice>
      )}
      {extra}
      <div>
        <h3 className="section-title">Timeline</h3>
        <ol className="timeline" style={{ marginTop: 10 }}>
          {cs.events.map((e, i) => (
            <li key={i}>
              <time dateTime={e.at}>{shortWhen(e.at)}</time>
              <span>{e.message}</span>
            </li>
          ))}
        </ol>
      </div>
      {cs.diagnostics && cs.diagnostics.length > 0 && (
        <div>
          <h3 className="section-title">Diagnostics (staff only)</h3>
          <pre className="diag" style={{ marginTop: 10 }}>
            {cs.diagnostics.map((d) => `${d.at}${d.stepId ? ` [${d.stepId}]` : ''}\n${d.detail}`).join('\n\n')}
          </pre>
        </div>
      )}
    </>
  );
}

/** Compact list of change sets: time, title, who, status. */
export function ChangeSetFeed({ items, onOpen, showTenant }: { items: ChangeSetView[]; onOpen: (cs: ChangeSetView) => void; showTenant?: boolean }) {
  return (
    <ol className="activity">
      {items.map((cs) => (
        <li key={cs.id}>
          <time dateTime={cs.createdAt}>{shortWhen(cs.createdAt)}</time>
          <button type="button" className="what" onClick={() => onOpen(cs)}>
            <span>{cs.title}</span>
            <small>
              {showTenant ? `${cs.tenant} · ` : ''}
              {actorLabel(cs)}
            </small>
          </button>
          <StatusChip status={cs.status} small />
        </li>
      ))}
    </ol>
  );
}
